// Phase 1D -- the Watchlist: "these are the questions Martian watches on my
// behalf". A first-class, founder-owned object inside Your Business.
// Three ways to add (import from Google Search Console -- real queries from
// the workspace's existing connection, reviewed and selected first, saved
// as SUGGESTED only; add my own -- comma/newline separated; let Martian
// suggest -- rebuilt from Company Context). Up to 100 questions. Primary
// action: Approve questions -- the only thing that switches weekly
// monitoring on (Phase 0 P0.7). Importing never approves anything.
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { somPromptsAPI, watchlistAPI } from '@/lib/api';
import { useFounderWorkspace, useFounderPath } from '../FounderWorkspace';
import { Chip, Disclosure, Loading, PageHeader, PrimaryButton, SecondaryButton, TextButton, Section, EmptyState } from '../ui';

const MAX = 100;
export const GSC_TAVILY_DISCLOSURE_VERSION = 'gsc_tavily_v1';
const SOURCE = { martian: 'Martian', user: 'You', gsc: 'Google Search Console' };
const INTENT = { best_pick: 'Best choice', comparison: 'Comparison', segment_recommendation: 'Recommendation', how_to_choose: 'How to choose' };
// Why a Search Console query was left out -- the server's fixed reason codes.
const GSC_REASON = {
  unclear: 'Not a readable search',
  too_short: 'Too short to be a question',
  too_long: 'Too long to be a question',
  navigational: 'Looking for a page, not an answer',
  mentions_your_brand: 'Already mentions your brand',
  not_about_your_business: 'Not about your business',
  duplicate_in_results: 'Same as another search above',
  already_in_watchlist: 'Already in your watchlist',
};

const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;
const formatCount = (n) => (typeof n === 'number' ? n.toLocaleString() : '—');
const formatCtr = (n) => (typeof n === 'number' ? `${(n * 100).toFixed(1)}%` : '—');
const formatPosition = (n) => (typeof n === 'number' ? n.toFixed(1) : '—');

function GscMetrics({ c }) {
  return (
    <span className="tabular-nums">
      {formatCount(c.clicks)} clicks · {formatCount(c.impressions)} impressions · {formatCtr(c.ctr)} CTR · position {formatPosition(c.position)}
    </span>
  );
}

// The import flow: read real queries -> review and select -> import as
// suggested -> show exactly what happened. Nothing is saved until Import.
function GscImportPanel({ gsc, selected, onToggle, onSelectAll, onClear, onImport, onClose, onRetry, busy, room, connectionsPath }) {
  if (gsc.status === 'loading') {
    return <p className="text-[13.5px] text-[var(--text-secondary)]" role="status" aria-live="polite" data-testid="watchlist-gsc-loading">Reading your Search Console queries…</p>;
  }
  if (gsc.status === 'error') {
    return (
      <div data-testid="watchlist-gsc-error" role="alert">
        <p className="text-[13.5px] text-[var(--text-primary)]">{gsc.message}</p>
        <div className="mt-2 flex gap-4"><TextButton tone="accent" onClick={onRetry}>Try again</TextButton><TextButton onClick={onClose}>Close</TextButton></div>
      </div>
    );
  }
  if (gsc.status === 'result') {
    const r = gsc.result;
    const parts = [
      r.skipped_duplicate ? `${r.skipped_duplicate} already in your watchlist` : null,
      r.excluded ? `${r.excluded} left out` : null,
      r.capacity_limited ? `${r.capacity_limited} over the ${MAX}-question limit` : null,
      r.unavailable ? `${r.unavailable} no longer in Search Console` : null,
    ].filter(Boolean);
    return (
      <div data-testid="watchlist-gsc-result" role="status">
        <p className="text-[14px] font-medium">Imported {plural(r.imported, 'question')} as suggested{parts.length ? ` · ${parts.join(' · ')}` : ''}.</p>
        <p className="mt-1 text-[13px] text-[var(--text-secondary)]" data-testid="watchlist-gsc-result-monitoring-note">Imported questions are not yet included in weekly checks, even after you approve them.</p>
        <div className="mt-2"><TextButton onClick={onClose}>Done</TextButton></div>
      </div>
    );
  }
  const data = gsc.data;
  if (data.state !== 'ready') {
    return (
      <div data-testid="watchlist-gsc-not-ready" data-state={data.state}>
        <p className="text-[13.5px] text-[var(--text-primary)]">{data.message}</p>
        <div className="mt-2 flex gap-4"><TextButton tone="accent" to={connectionsPath}>Open Connections</TextButton><TextButton onClick={onClose}>Close</TextButton></div>
      </div>
    );
  }
  const period = data.window ? `${data.window.start_date} to ${data.window.end_date}` : '';
  const excluded = data.excluded || [];
  return (
    <div data-testid="watchlist-gsc-candidates">
      <p className="text-[13px] text-[var(--text-secondary)]">
        {data.site_url} · searches from {period}{data.truncated ? ` · showing your top ${data.rows_read} searches only` : ''}
      </p>
      {data.candidates.length === 0 ? (
        <p className="mt-3 text-[13.5px] text-[var(--text-primary)]" data-testid="watchlist-gsc-empty">
          {data.rows_read ? 'No new questions in these searches.' : 'Search Console has no searches for this period yet.'}
        </p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-[13px]">
            <span className="text-[var(--text-secondary)]" data-testid="watchlist-gsc-selected">{selected.size} of {data.candidates.length} selected · room for {room} more</span>
            <TextButton onClick={onSelectAll} disabled={busy}>Select all</TextButton>
            <TextButton onClick={onClear} disabled={busy || !selected.size}>Clear</TextButton>
          </div>
          <ul className="mt-3 divide-y divide-[var(--border)] border-y border-[var(--border)]">
            {data.candidates.map((c) => (
              <li key={c.candidate_id} data-testid="watchlist-gsc-candidate">
                <label className="flex items-start gap-3 py-3 cursor-pointer">
                  <input type="checkbox" className="mt-1 accent-[var(--accent)]" checked={selected.has(c.candidate_id)} onChange={() => onToggle(c.candidate_id)} disabled={busy} />
                  <span className="min-w-0">
                    <span className="block text-[14px] font-medium leading-snug">{c.question}</span>
                    <span className="block mt-0.5 text-[12px] text-[var(--text-tertiary)]">Searched as “{c.original_query}” · <GscMetrics c={c} /></span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {selected.size > room && (
            <p className="mt-2 text-[13px] text-[var(--martian-yellow-ink,#7A5F12)]" data-testid="watchlist-gsc-over-room">Only {room} more fit in your watchlist; the rest won't be imported.</p>
          )}
        </>
      )}
      {excluded.length > 0 && (
        <div className="mt-4">
          <Disclosure label={`${plural(excluded.length, 'search', 'searches')} left out`} testId="watchlist-gsc-excluded">
            <ul className="space-y-1.5 text-[12.5px] text-[var(--text-secondary)]">
              {excluded.map((e) => (
                <li key={e.candidate_id}>“{e.original_query}” — {GSC_REASON[e.reason] || e.reason}</li>
              ))}
            </ul>
          </Disclosure>
        </div>
      )}
      <p className="mt-4 text-[12.5px] text-[var(--text-tertiary)]" data-testid="watchlist-gsc-monitoring-note">Imported questions are saved as suggested. Even after approval, imported questions are not yet included in weekly checks.</p>
      <div className="mt-3 flex items-center gap-4">
        {data.candidates.length > 0 && (
          <SecondaryButton onClick={onImport} disabled={busy || !selected.size || room <= 0} testId="watchlist-gsc-import">
            Import {plural(selected.size, 'question')}
          </SecondaryButton>
        )}
        <TextButton onClick={onClose} disabled={busy}>Cancel</TextButton>
      </div>
    </div>
  );
}

export function isGscDerivedPrompt(q) {
  const ep = q?.external_processing;
  if (ep && (ep.consent_required || ep.origin === 'google_search_console' || ep.origin === 'ambiguous')) return true;
  const tags = Array.isArray(q?.tags) ? q.tags : [];
  const prov = q?.gsc_provenance;
  return q?.source === 'gsc'
    || q?.metadata?.import_source === 'google_search_console'
    || tags.includes('gsc_import')
    || (prov && typeof prov === 'object' && (prov.provider === 'google_search_console' || Boolean(prov.query)));
}

export function gscTavilyAuthorized(q) {
  if (!isGscDerivedPrompt(q)) return true;
  if (q?.external_processing && typeof q.external_processing.authorized === 'boolean') {
    return q.external_processing.authorized;
  }
  const record = q?.external_processing_consent;
  const text = q?.prompt_text || q?.question;
  return record?.status === 'granted' && record?.consented_query_text === text;
}

function nonEmptyId(value) {
  return typeof value === 'string' && value.trim() !== '';
}

// The provider runs prompt_text. Surrounding whitespace is not the same
// string the consent record is compared against, so it is not runnable.
export function canonicalGscRunQuery(q) {
  const text = q && q.prompt_text;
  if (typeof text !== 'string') return '';
  const canonical = text.trim();
  if (!canonical || canonical !== text) return '';
  return canonical;
}

// Usability gate only. The server still checks tenant, workspace, prompt,
// query text, and disclosure version before any Tavily call.
export function canRunGscOnce(q) {
  if (!q || q.active !== true || q.watchlist_status !== 'approved') return false;
  if ((q.external_processing || {}).origin !== 'google_search_console') return false;
  if ((q.metadata || {}).source !== 'som_custom') return false;
  const record = q.external_processing_consent;
  if (!record || typeof record !== 'object') return false;
  const query = canonicalGscRunQuery(q);
  if (!query || record.consented_query_text !== query) return false;
  if (record.status !== 'granted' || record.disclosure_version !== GSC_TAVILY_DISCLOSURE_VERSION) return false;
  if (!nonEmptyId(q.tenant_id) || !nonEmptyId(record.tenant_id) || record.tenant_id !== q.tenant_id) return false;
  if (!nonEmptyId(q.client_id) || !nonEmptyId(record.client_id) || record.client_id !== q.client_id) return false;
  if (!nonEmptyId(q.id) || !nonEmptyId(record.prompt_id) || record.prompt_id !== q.id) return false;
  return true;
}

const RUN_ONCE_AMBIGUOUS = 'This run did not finish clearly. It was not tried again.';

export function classifyGscRunOnce(data) {
  const status = data && typeof data.status === 'string' ? data.status : '';
  if (status === 'completed') {
    return { outcome: 'success', message: 'Ran once. This query text was sent to Tavily for the consented AI visibility check.' };
  }
  if (status === 'failed' || status === 'blocked' || status === 'rejected' || status === 'error') {
    const detail = data.message || data.error || data.error_code;
    return { outcome: 'failure', message: typeof detail === 'string' && detail.trim() ? detail.trim() : 'This check did not complete.' };
  }
  return { outcome: 'ambiguous', message: RUN_ONCE_AMBIGUOUS };
}

export function classifyGscRunOnceError(err) {
  const code = err && err.code;
  if (!err || !err.response || code === 'ECONNABORTED' || code === 'ERR_NETWORK' || code === 'ETIMEDOUT') {
    return { outcome: 'ambiguous', message: RUN_ONCE_AMBIGUOUS };
  }
  const detail = err.response.data && err.response.data.detail;
  const message = typeof detail === 'string' ? detail : detail && (detail.message || detail.error_code);
  if (typeof message === 'string' && message.trim()) return { outcome: 'failure', message: message.trim() };
  return { outcome: 'ambiguous', message: RUN_ONCE_AMBIGUOUS };
}

function trackingStatus(q, monitoring) {
  if (!q.active) return { label: 'Paused', tone: 'neutral' };
  const custom = q.metadata?.source === 'som_custom';
  const gscBlocked = isGscDerivedPrompt(q) && !gscTavilyAuthorized(q);
  if (q.watchlist_status !== 'approved') {
    return gscBlocked
      ? { label: 'Suggested · Tavily permission needed', tone: 'warn' }
      : { label: 'Suggested', tone: 'warn' };
  }
  if (gscBlocked) return { label: 'Not sent to Tavily', tone: 'warn' };
  if (custom) return { label: 'Saved · not in weekly checks yet', tone: 'neutral' };
  return monitoring?.recurring ? { label: 'Checked weekly', tone: 'good' } : { label: 'Approved', tone: 'good' };
}

function GscTavilyConsent({ q, acknowledged, onToggle, onGrant, onRevoke, busy }) {
  const disclosure = q.external_processing?.disclosure || {};
  const version = disclosure.version || GSC_TAVILY_DISCLOSURE_VERSION;
  if (gscTavilyAuthorized(q)) {
    return (
      <div className="mt-2 text-[12.5px] text-[var(--text-secondary)]" data-testid="watchlist-gsc-tavily-granted">
        <p>This Search Console query may be sent to Tavily for AI visibility checks. Clicks, impressions and other Search Console metrics are not sent.</p>
        <div className="mt-1"><TextButton onClick={() => onRevoke(q)} disabled={busy} testId={`watchlist-gsc-tavily-revoke-${q.id}`}>Stop sending this query to Tavily</TextButton></div>
      </div>
    );
  }
  return (
    <div className="mt-2 rounded-[var(--radius-card-soft)] border border-[var(--border)] bg-[var(--surface-sunken)] p-3" data-testid="watchlist-gsc-tavily-consent">
      <p className="text-[12.5px] text-[var(--text-primary)]" data-testid="watchlist-gsc-tavily-blocked">
        AI visibility checks for this Search Console query are blocked until you opt in. Martian would send the query text to Tavily, an external search provider, to generate search/AI visibility monitoring results. Clicks, impressions, tokens and other Search Console provenance are not sent.
      </p>
      <label className="mt-2 flex items-start gap-2 cursor-pointer text-[12.5px] text-[var(--text-secondary)]">
        <input
          type="checkbox"
          className="mt-0.5 accent-[var(--accent)]"
          checked={Boolean(acknowledged)}
          onChange={onToggle}
          disabled={busy}
          data-testid="watchlist-gsc-tavily-ack"
        />
        <span>I understand this query text will be sent to Tavily for AI visibility monitoring ({version}).</span>
      </label>
      <div className="mt-2">
        <SecondaryButton onClick={() => onGrant(q)} disabled={busy || !acknowledged} testId="watchlist-gsc-tavily-grant">
          Allow Tavily to receive this query
        </SecondaryButton>
      </div>
    </div>
  );
}

export function parseQuestions(text) {
  return Array.from(new Set(
    (text || '').split(/[\n,]/).map((s) => s.trim()).filter((s) => s.length >= 4),
  ));
}

export default function WatchlistPage() {
  const { workspaceId, summary, refreshSummary, loadingClients } = useFounderWorkspace();
  const fp = useFounderPath();
  const [questions, setQuestions] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [gsc, setGsc] = useState(null); // null | { status: 'loading' | 'ready' | 'error' | 'result', ... }
  const [gscSelected, setGscSelected] = useState(() => new Set());
  const [tavilyAck, setTavilyAck] = useState(() => new Set());
  const [runConfirmId, setRunConfirmId] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const [runOutcomes, setRunOutcomes] = useState({});
  const runningPromptId = useRef(null);
  // A Search Console read or import belongs to the workspace it started in.
  // Switching workspace bumps this token, so a late answer (success or
  // error) for the previous workspace is dropped instead of shown here.
  const gscRequest = useRef(0);

  const load = useCallback(() => {
    if (!workspaceId) return Promise.resolve();
    return somPromptsAPI.list(workspaceId).then((r) => setQuestions(r.data?.prompts || [])).catch(() => setQuestions([]));
  }, [workspaceId]);
  useEffect(() => { gscRequest.current += 1; setQuestions(null); setGsc(null); setGscSelected(new Set()); setRunConfirmId(null); setRunOutcomes({}); load(); }, [load]);

  if (loadingClients || (workspaceId && questions === null)) return <Loading />;
  const monitoring = summary?.monitoring;
  const suggested = questions.filter((q) => q.active && q.watchlist_status !== 'approved');
  const approvedCount = questions.filter((q) => q.active && q.watchlist_status === 'approved').length;
  const room = MAX - questions.filter((q) => q.active).length;

  const approve = async (ids, ok = true) => {
    setBusy(true);
    try {
      const res = await watchlistAPI.decide(workspaceId, ids, ok);
      toast.success(ok ? (res.data?.monitoring_enabled ? "Approved. Weekly checks are on for Martian's four core questions. Imported and custom questions aren't included yet." : 'Approved') : 'Removed from weekly checks');
      load();
      refreshSummary();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save that — please try again"); } finally { setBusy(false); }
  };

  const grantTavily = async (q) => {
    if (!tavilyAck.has(q.id)) return;
    setBusy(true);
    try {
      const version = q.external_processing?.disclosure?.version || GSC_TAVILY_DISCLOSURE_VERSION;
      const res = await watchlistAPI.setExternalProcessingConsent(workspaceId, q.id, {
        granted: true, disclosureVersion: version, acknowledged: true,
      });
      const updated = res.data;
      setQuestions((prev) => (prev || []).map((row) => (row.id === q.id ? { ...row, ...updated } : row)));
      setTavilyAck((prev) => { const next = new Set(prev); next.delete(q.id); return next; });
      toast.success('Tavily may receive this Search Console query for AI visibility checks');
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((detail && (detail.message || detail)) || "Couldn't save that permission — please try again");
    } finally { setBusy(false); }
  };

  const revokeTavily = async (q) => {
    setBusy(true);
    try {
      const res = await watchlistAPI.setExternalProcessingConsent(workspaceId, q.id, {
        granted: false, disclosureVersion: GSC_TAVILY_DISCLOSURE_VERSION, acknowledged: false,
      });
      const updated = res.data;
      setQuestions((prev) => (prev || []).map((row) => (row.id === q.id ? { ...row, ...updated } : row)));
      toast.success('This Search Console query will not be sent to Tavily');
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((detail && (detail.message || detail)) || "Couldn't revoke that permission — please try again");
    } finally { setBusy(false); }
  };

  const addMine = async () => {
    const items = parseQuestions(draft);
    if (!items.length) return;
    if (items.length > room) { toast.error(`You can add ${room} more question${room === 1 ? '' : 's'}.`); return; }
    setBusy(true);
    const created = [];
    const failed = [];
    for (const text of items) {
      try { const r = await somPromptsAPI.createCustom(workspaceId, { prompt_text: text, priority: 'medium' }); created.push(r.data.id); }
      catch (e) { failed.push(e?.response?.data?.detail || text); }
    }
    if (created.length) await watchlistAPI.decide(workspaceId, created, true).catch(() => {});
    setBusy(false);
    if (created.length) toast.success(`Added ${created.length} question${created.length === 1 ? '' : 's'}`);
    if (failed.length) toast.error(failed[0]);
    setDraft('');
    setAdding(false);
    load();
    refreshSummary();
  };

  const openGsc = async () => {
    gscRequest.current += 1;
    const request = gscRequest.current;
    setGsc({ status: 'loading' });
    setGscSelected(new Set());
    try {
      const r = await watchlistAPI.gscCandidates(workspaceId);
      if (request !== gscRequest.current) return;
      setGsc({ status: 'ready', data: r.data });
    } catch (e) {
      if (request !== gscRequest.current) return;
      setGsc({ status: 'error', message: e?.response?.data?.detail || "Couldn't read Search Console right now. Please try again." });
    }
  };

  const toggleGsc = (id) => setGscSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const importGsc = async () => {
    if (!gscSelected.size) return;
    const request = gscRequest.current;
    setBusy(true);
    try {
      const r = await watchlistAPI.gscImport(workspaceId, Array.from(gscSelected));
      // Saved for the workspace it started in; if the founder has since
      // switched, don't show that result (or reload that list) here.
      if (request !== gscRequest.current) return;
      setGsc({ status: 'result', result: r.data });
      setGscSelected(new Set());
      if (r.data?.imported) toast.success(`Imported ${plural(r.data.imported, 'question')} as suggested`);
      load();
      refreshSummary();
    } catch (e) {
      if (request !== gscRequest.current) return;
      toast.error(e?.response?.data?.detail || "Couldn't import those questions — please try again");
    } finally { setBusy(false); }
  };

  const confirmRunOnce = async (q) => {
    if (runningPromptId.current || !canRunGscOnce(q) || !workspaceId || !q.id) return;
    runningPromptId.current = q.id;
    setRunningId(q.id);
    const workspaceAtStart = workspaceId;
    const token = gscRequest.current;
    try {
      const res = await somPromptsAPI.runNow(workspaceAtStart, q.id);
      if (token !== gscRequest.current) return;
      const outcome = classifyGscRunOnce(res && res.data);
      setRunOutcomes((prev) => ({ ...prev, [q.id]: outcome }));
      if (outcome.outcome === 'success') toast.success(outcome.message);
      else toast.error(outcome.message);
    } catch (err) {
      if (token !== gscRequest.current) return;
      const outcome = classifyGscRunOnceError(err);
      setRunOutcomes((prev) => ({ ...prev, [q.id]: outcome }));
      toast.error(outcome.message);
    } finally {
      if (runningPromptId.current === q.id) runningPromptId.current = null;
      setRunningId((current) => (current === q.id ? null : current));
      setRunConfirmId((current) => (current === q.id ? null : current));
    }
  };

  const suggestAgain = async () => {
    setBusy(true);
    try { await watchlistAPI.suggest(workspaceId); toast.success("Martian's questions are up to date with your business"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Martian couldn't write questions yet"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="watchlist-page">
      <PageHeader
        eyebrow="Your Business · Watchlist"
        title="Questions Martian watches for you"
        summary={monitoring?.recurring
          ? 'Martian asks AI assistants these questions every week and tells you when the answers change.'
          : 'These are the questions your customers ask AI assistants. Approve them and Martian checks the answers every week.'}
        primary={suggested.length > 0 && (
          <PrimaryButton onClick={() => approve(suggested.map((q) => q.id))} disabled={busy} testId="watchlist-approve-all">
            Approve {suggested.length} question{suggested.length === 1 ? '' : 's'}
          </PrimaryButton>
        )}
        secondary={<TextButton to={fp('/business')}>Back to Your Business</TextButton>}
      />

      <p className="mb-4 text-[13px] text-[var(--text-secondary)]" data-testid="watchlist-progress">
        {approvedCount} of {questions.filter((q) => q.active).length} approved · room for {room} more
      </p>

      {questions.length === 0 ? (
        <EmptyState title="No questions yet" body="Let Martian suggest questions from your business, or add your own." />
      ) : (
        <div className="overflow-x-auto -mx-5 md:mx-0">
          <table className="w-full min-w-[720px] text-[13.5px]" data-testid="watchlist-table">
            <thead>
              <tr className="text-left text-[12px] text-[var(--text-tertiary)] border-b border-[var(--border)]">
                <th className="py-2 pl-5 md:pl-0 font-medium">Question</th>
                <th className="py-2 font-medium">Source</th>
                <th className="py-2 font-medium">Market</th>
                <th className="py-2 font-medium">Segment</th>
                <th className="py-2 font-medium">Priority</th>
                <th className="py-2 font-medium">Tracking</th>
                <th className="py-2 pr-5 md:pr-0"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {questions.map((q) => {
                const st = trackingStatus(q, monitoring);
                const src = q.source || (q.metadata?.source === 'som_custom' ? 'user' : 'martian');
                const rowRunning = runningId === q.id;
                const queryText = q.question || q.prompt_text;
                const canonicalQuery = canonicalGscRunQuery(q);
                const runOutcome = runOutcomes[q.id];
                return (
                  <tr key={q.id} data-testid="watchlist-row" className="align-top">
                    <td className="py-3 pl-5 md:pl-0 pr-4">
                      <p className="font-medium text-[14px] leading-snug">{queryText}</p>
                      {q.gsc_provenance?.query && <p className="text-[12px] text-[var(--text-tertiary)] mt-0.5" data-testid="watchlist-row-gsc-query">Searched as “{q.gsc_provenance.query}”</p>}
                      {isGscDerivedPrompt(q) && (
                        <GscTavilyConsent
                          q={q}
                          acknowledged={tavilyAck.has(q.id)}
                          onToggle={() => setTavilyAck((prev) => {
                            const next = new Set(prev);
                            if (next.has(q.id)) next.delete(q.id); else next.add(q.id);
                            return next;
                          })}
                          onGrant={grantTavily}
                          onRevoke={revokeTavily}
                          busy={busy || rowRunning}
                        />
                      )}
                      {canRunGscOnce(q) && runConfirmId === q.id && (
                        <div className="mt-2 rounded-[var(--radius-card-soft)] border border-[var(--border)] bg-[var(--surface-sunken)] p-3" data-testid={`watchlist-gsc-run-confirm-${q.id}`}>
                          <p className="text-[12.5px] text-[var(--text-primary)]">
                            Run this check once. Martian will send this query text to Tavily for the consented AI visibility check: “{canonicalQuery}”. Clicks, impressions, and other Search Console metrics are not sent. This does not turn on weekly checks.
                          </p>
                          <div className="mt-2 flex gap-3">
                            <SecondaryButton onClick={() => confirmRunOnce(q)} disabled={rowRunning} testId={`watchlist-gsc-run-submit-${q.id}`}>Run once</SecondaryButton>
                            <TextButton onClick={() => setRunConfirmId(null)} disabled={rowRunning} testId={`watchlist-gsc-run-cancel-${q.id}`}>Cancel</TextButton>
                          </div>
                        </div>
                      )}
                      {canRunGscOnce(q) && runConfirmId !== q.id && (
                        <div className="mt-2">
                          <TextButton tone="accent" onClick={() => setRunConfirmId(q.id)} disabled={busy || !!runningId} testId={`watchlist-gsc-run-once-${q.id}`}>Run once</TextButton>
                        </div>
                      )}
                      {runOutcome && (
                        <p className="mt-2 text-[12.5px] text-[var(--text-secondary)]" data-testid={`watchlist-gsc-run-result-${q.id}`} data-outcome={runOutcome.outcome}>{runOutcome.message}</p>
                      )}
                      {q.intent && INTENT[q.intent] && <p className="text-[12px] text-[var(--text-tertiary)] mt-0.5">{INTENT[q.intent]}{q.category && q.category !== 'som_custom' ? ` · ${q.category}` : ''}</p>}
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap">{SOURCE[src] || src}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">{q.market || '—'}</td>
                    <td className="py-3 pr-4">{q.segment || '—'}</td>
                    <td className="py-3 pr-4 capitalize">{q.priority || '—'}</td>
                    <td className="py-3 pr-4"><Chip tone={st.tone}>{st.label}</Chip></td>
                    <td className="py-3 pr-5 md:pr-0 text-right whitespace-nowrap">
                      {q.active && q.watchlist_status === 'approved'
                        ? <TextButton onClick={() => approve([q.id], false)} disabled={busy || rowRunning}>Stop watching</TextButton>
                        : q.active && <TextButton tone="accent" onClick={() => approve([q.id])} disabled={busy || rowRunning}>Approve</TextButton>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Section eyebrow="Add questions" testId="watchlist-add" className="mt-8">
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <p className="text-[15px] font-semibold">Import from Google Search Console</p>
            <p className="mt-1 text-[13.5px] text-[var(--text-secondary)]">The questions people really search for. You choose which to keep.</p>
            <div className="mt-3"><SecondaryButton onClick={openGsc} disabled={busy || gsc?.status === 'loading'} testId="watchlist-import-gsc">Review searches</SecondaryButton></div>
          </div>
          <div>
            <p className="text-[15px] font-semibold">Add your own</p>
            <p className="mt-1 text-[13.5px] text-[var(--text-secondary)]">One per line, or separated by commas.</p>
            <div className="mt-3">
              {adding ? (
                <div>
                  <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={4} aria-label="Questions to add" data-testid="watchlist-add-input"
                    placeholder="Which eSIM works best in Japan?"
                    className="w-full rounded-[var(--radius-input)] border border-[var(--border)] bg-[var(--surface-sunken)] px-3 py-2 text-[14px] focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]" />
                  <p className="mt-1 text-[12px] text-[var(--text-tertiary)]">{parseQuestions(draft).length} question{parseQuestions(draft).length === 1 ? '' : 's'}</p>
                  <div className="mt-2 flex gap-3"><SecondaryButton onClick={addMine} disabled={busy || !parseQuestions(draft).length} testId="watchlist-add-save">Add</SecondaryButton><TextButton onClick={() => setAdding(false)}>Cancel</TextButton></div>
                </div>
              ) : <SecondaryButton onClick={() => setAdding(true)} disabled={room <= 0} testId="watchlist-add-open">Add questions</SecondaryButton>}
            </div>
          </div>
          <div>
            <p className="text-[15px] font-semibold">Let Martian suggest</p>
            <p className="mt-1 text-[13.5px] text-[var(--text-secondary)]">Written from what Martian knows about your business.</p>
            <div className="mt-3"><SecondaryButton onClick={suggestAgain} disabled={busy} testId="watchlist-suggest">Refresh suggestions</SecondaryButton></div>
          </div>
        </div>
        {gsc && (
          <div className="mt-6 rounded-[var(--radius-card-soft)] border border-[var(--border)] bg-[var(--surface)] p-4 md:p-5" data-testid="watchlist-gsc-panel">
            <p className="text-[15px] font-semibold mb-2">Searches from Google Search Console</p>
            <GscImportPanel
              gsc={gsc}
              selected={gscSelected}
              onToggle={toggleGsc}
              onSelectAll={() => setGscSelected(new Set((gsc.data?.candidates || []).map((c) => c.candidate_id)))}
              onClear={() => setGscSelected(new Set())}
              onImport={importGsc}
              onClose={() => { setGsc(null); setGscSelected(new Set()); }}
              onRetry={openGsc}
              busy={busy}
              room={room}
              connectionsPath={fp('/evidence/connections')}
            />
          </div>
        )}
      </Section>

      <p className="mt-6 text-[12.5px] text-[var(--text-tertiary)] max-w-[70ch]" data-testid="watchlist-coverage-note">
        Your own and imported questions are saved to the watchlist; Martian's weekly checks currently cover the questions Martian wrote.
      </p>
    </div>
  );
}
